import { CustomDisclosure } from 'components';
import { FC, useEffect } from 'react';
import { PermissionSettingType } from '../constants';
import { PermissionItem } from '../EditPermissionsModal';
import PermissionListItem from './PermissionListItem';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectSelectedPermission } from '../editPermissionsSlice';
import { getNotifications, showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';

interface PermissionDisclosureProps {
  className?: string;
  title: PermissionSettingType;
  secondaryTitle: string;
  permissionsSettingsList: PermissionItem[];
  onTogglePermission: (teamPermission: PermissionItem) => void;
}

const PermissionDisclosure: FC<PermissionDisclosureProps> = ({
  title,
  onTogglePermission,
  secondaryTitle,
  permissionsSettingsList,
  className = ''
}) => {
  const selectedPermission = useAppSelector(selectSelectedPermission);
  const notifications = useAppSelector(getNotifications);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (selectedPermission === title) {
      onScrollSectionIntoView();
    }
  }, [selectedPermission]);

  const onScrollSectionIntoView = () => {
    const permissionSectionElement = document.getElementById(title);
    setTimeout(() => {
      permissionSectionElement?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }, 100);
  };

  const showAlert = (message: string) => {
    const existingNotification = notifications.find(notification => notification.id === 'permission-disclosure-alert');
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id: 'permission-disclosure-alert',
        message: {
          primaryMessage: message,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const handleTogglePermission = (permission: PermissionItem) => {
    const shouldShowAlert =
      permission.alertMessage &&
      ((!permission.enabled && permission.showMessageOnEnable) ||
        (permission.enabled && !permission.showMessageOnEnable));

    if (shouldShowAlert) {
      showAlert(permission.alertMessage);
      return;
    }

    onTogglePermission(permission);
  };

  return (
    <CustomDisclosure
      onContainerClick={onScrollSectionIntoView}
      id={title}
      secondaryTitleStyle="text-tertiaryText"
      title={title}
      titleStyle="text-secondaryBtnText"
      secondaryTitle={secondaryTitle}
      containerStyle="mb-5 border border-dark-gray rounded-md"
      isOpen={selectedPermission === title}>
      <div className="flex flex-col">
        {permissionsSettingsList.map(permission => {
          return (
            <PermissionListItem
              className={className}
              key={permission.key}
              onTogglePemission={() => handleTogglePermission(permission)}
              enabled={permission.enabled}
              title={permission.title}
            />
          );
        })}
      </div>
    </CustomDisclosure>
  );
};

export default PermissionDisclosure;
